import { Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from 'src/reducers';
import { DRAW_MODAL } from 'src/reducers/modal/types';

import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { getFromLang } from 'Utilities/newyu';
import { store } from 'Utilities/store';
import * as yup from 'Utilities/yup';

import { OK_MODAL } from 'Constants/modalTypes';

import LibreAppWrapper from 'Containers/LibreAppWrapper';

import HtmlTranslation from 'Components/HtmlTranslation';
import ErrorField from 'Components/input/ErrorField';
import List from 'Components/List';
import PracticeDetails from 'Components/PracticeDetails';

import greenCheckImg from 'Images/green-check.svg';
import LVLogoSmall from 'Images/logos/libreView-small.svg';
import LVLogoSmallAR from 'Images/logos/libreView-small-ar.svg';
import noPracticesImg from 'Images/no-practices.svg';

const connectToPractice = (event: React.MouseEvent, redirect?: boolean) => {
  mediator.publish(redirect ? 'router:redirect' : 'router:navigate', '/libreview/connectTo');
};

const goToMain = () => {
  mediator.publish('router:navigate', '/libreview');
};

const goToConnected = (id: string) => {
  mediator.publish('router:navigate', '/libreview/connected/' + id);
};

const goToInvite = (id: string) => {
  mediator.publish('router:navigate', '/libreview/pending/' + id);
};

const inviteDecline = (event: React.MouseEvent, practice: Practice) => {
  mediator.publish('practice:inviteDeclineModal', {
    inviteId: practice.invitationId,
    callback: (result: LVApiResponse) => {
      if (result.status === 0) {
        goToMain();
      }
    },
  });
};

const learnMore = () => {
  store.dispatch({
    type: DRAW_MODAL,
    modal: OK_MODAL,
    props: {
      body: (
        <div className="d-flex flex-column">
          <span className="text-black mb-2">{i18n.t('LV.learnMoreMsg1')}</span>
          <span className="text-black mb-3">{i18n.t('LV.learnMoreMsg2')}</span>
          <span className="text-md">*{i18n.t('LV.learnMoreDisclaimer1')}</span>
        </div>
      ),
    },
  });
};

const stopSharing = (event: React.MouseEvent, practiceId: string) => {
  if (practiceId) {
    mediator.publish('practice:stopSharingModal', {
      practiceId,
      callback: (result: LVApiResponse) => {
        if (result.status === 0) {
          goToMain();
        }
      },
    });
  }
};

const mapState = ({
  config: { lv, privacyPolicy },
  nav: { location },
  practice: { current, pending },
  user: { phoneLanguage, practices },
}: RootState) => {
  return {
    atConnected: !!location.match(/^\/libreview\/connected/i),
    atConnectTo: !!location.match(/^\/libreview\/connectTo$/i),
    atConnectToDetails: !!location.match(/^\/libreview\/connectTo\//i),
    atMain: !!location.match(/^\/libreview\/?$/i),
    atPending: !!location.match(/^\/libreview\/pending\//i),
    connected: Object.values(practices),
    practiceDetails: current,
    currentPracticeId: location.split('/')[3],
    libreView: {
      id: 'libreview',
      transGroupKey: 'LV',
      logo: phoneLanguage.startsWith('ar') ? LVLogoSmallAR : LVLogoSmall,
    },
    // Ensure pending does not display practices that are connected
    pending: Object.values(pending).filter((p) => !Object.keys(practices).includes(p.id)),
    privacyNoticeUrl: lv + getFromLang(privacyPolicy, phoneLanguage),
  };
};

const connector = connect(mapState);

type Props = ConnectedProps<typeof connector>;

const LibreView: React.FC<React.PropsWithChildren<Props>> = ({
  atConnected,
  atConnectTo,
  atConnectToDetails,
  atMain,
  atPending,
  connected,
  currentPracticeId,
  libreView,
  pending,
  practiceDetails,
  privacyNoticeUrl,
}) => {
  const [initialPracticeId, setInitialPracticeId] = useState('');
  const [error, setError] = useState({
    connectError: false,
    submitDisabled: false,
  });

  const selectedPractice = useMemo(() => connected.find((p) => p.id === currentPracticeId), [
    connected,
    currentPracticeId,
  ]);
  const selectedInvite = useMemo(() => pending.find((p) => p.id === currentPracticeId), [
    pending,
    currentPracticeId,
  ]);

  const confirmPracticeConnect = () => {
    if (practiceDetails) {
      mediator
        .request<LVApiResponse<Practice>>('practice:link', {
          practiceId: practiceDetails.practiceId,
        })
        .then((result) => {
          if (result.status === 0) {
            mediator.publish('router:navigate', '/libreview/connected/' + practiceDetails.id);
          } else if (result.status === 3) {
            setError({
              connectError: true,
              submitDisabled: true,
            });
          }
        });
    }
  };

  const inviteAccept = (event: React.MouseEvent, practice: Practice) => {
    mediator
      .request<LVApiResponse>('practice:inviteAccept', { inviteId: practice.invitationId })
      .then((result) => {
        if (result.status === 0) {
          mediator.publish('router:navigate', '/libreview/connected/' + practice.id);
        } else if (result.status === 3) {
          setError({
            connectError: true,
            submitDisabled: true,
          });
        }
      });
  };

  let content: React.ReactNode = null;

  // Set the initial practice ID for the connect form when previewing the practice details
  useEffect(() => {
    if (!(atConnectToDetails || atPending) && error.connectError) {
      setError({
        connectError: false,
        submitDisabled: false,
      });
    }

    if (!atConnectTo && !atConnectToDetails) {
      setInitialPracticeId('');
    } else if (atConnectToDetails) {
      setInitialPracticeId(practiceDetails?.practiceId || '');
    }
  }, [atConnectTo, atConnectToDetails, practiceDetails, atPending, error]);

  // Fetch the connect-to practice details when browser is refreshed on the preview screen
  useEffect(() => {
    if (atConnectToDetails && !practiceDetails) {
      mediator.publish('practice:getByPracticeId', { practiceId: currentPracticeId });
    }
  }, [atConnectToDetails, currentPracticeId, practiceDetails]);

  if (atMain) {
    content = (
      <>
        {/* Main description learn more */}
        <span id="mainDesc-text" className="line-height-xxl mb-2">
          {i18n.t([`${libreView.transGroupKey}.mainDesc`, `${libreView.transGroupKey}.brandDesc`])}
        </span>
        <button
          id="learnMore-link"
          className="btn btn-link align-self-start line-height-xxl p-0 mb-4"
          onClick={learnMore}
        >
          {i18n.t('Common.learnMoreAbout', {
            name: i18n.t(`${libreView.transGroupKey}.brandName`),
          })}
        </button>

        {connected.length > 0 || pending.length > 0 ? (
          <>
            {pending.length > 0 && (
              <List
                id="pendingPracticesList"
                className="mb-3"
                title={i18n.t('LV.pendingInvitations', { context: 'cap' })}
                itemAction={goToInvite}
                items={pending}
              />
            )}
            {connected.length > 0 && (
              <List
                id="connectedPracticesList"
                className="mb-3"
                title={i18n.t('LV.connectedPractices', { context: 'cap' })}
                itemAction={goToConnected}
                items={connected}
              />
            )}
          </>
        ) : (
          <div className="d-flex flex-column align-items-center w-100">
            <img src={noPracticesImg} />
            <span id="noPractices-text" className="font-weight-bold mt-2 pb-3">
              {i18n.t('LV.noPractices')}
            </span>
          </div>
        )}

        <button
          id="connectToPractice-btn"
          className="btn btn-lg btn-primary w-100 mt-3"
          onClick={connectToPractice}
        >
          {i18n.t('LV.connectToPractice', { context: 'cap' })}
        </button>
      </>
    );
  } else if (atConnected) {
    if (selectedPractice) {
      content = (
        <div id={`connectedPractice-${currentPracticeId}`} className="d-flex flex-column">
          <div className="d-flex flex-column mb-4">
            <div className="d-flex mb-2">
              <span id="connectedHeader-text" className="text-black text-xl line-height-md">
                {i18n.t('Common.connected')}
              </span>
              <img className="ml-2" src={greenCheckImg} />
            </div>
            <span id="connectedDesc-text" className="text-soft line-height-xl">
              {i18n.t('LV.connectedDesc')}
            </span>
          </div>

          <PracticeDetails {...selectedPractice} />

          <div className="d-flex justify-content-end mt-3">
            <button
              id="stopSharing-btn"
              className="btn btn-link p-0"
              onClick={(e) => stopSharing(e, currentPracticeId)}
            >
              {i18n.t('Common.stopSharing', { context: 'cap' })}
            </button>
          </div>

          <button id="backToApp-btn" className="btn btn-lg btn-primary mt-4" onClick={goToMain}>
            {i18n.t('Common.done', { context: 'cap' })}
          </button>
        </div>
      );
    }
  } else if (atConnectTo) {
    content = (
      <Formik
        initialValues={{
          practiceId: initialPracticeId,
        }}
        initialStatus={['practiceId']}
        validationSchema={yup.object({
          practiceId: yup
            .string()
            .trim()
            .required(i18n.t('Errors.invalidPracticeId'))
            .min(8, i18n.t('Errors.invalidPracticeId'))
            .max(16, i18n.t('Errors.invalidPracticeId')),
        })}
        onSubmit={(values, formikHelpers) => {
          if (
            connected.find((c) => c.practiceId?.toLowerCase() === values.practiceId.toLowerCase())
          ) {
            formikHelpers.setFieldError('practiceId', i18n.t('Errors.alreadyConnectedPractice'));
            formikHelpers.setSubmitting(false);
            return;
          }

          mediator
            .request<LVApiResponse>('practice:getByPracticeId', {
              practiceId: values.practiceId,
            })
            .then((result) => {
              if (result.status === 0) {
                setInitialPracticeId(values.practiceId);
                mediator.publish(
                  'router:navigate',
                  '/libreview/connectTo/' + result.data.practiceId
                );
                return;
              } else if (result.status === 1) {
                formikHelpers.setFieldError('practiceId', i18n.t('Errors.wrongRegionPractice'));
              } else {
                formikHelpers.setFieldError('practiceId', i18n.t('Errors.invalidPracticeId'));
              }

              formikHelpers.setSubmitting(false);
            })
            .catch(() => {
              formikHelpers.setSubmitting(false);
            });
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="d-flex flex-column">
              <span id="enterPracticeId-text" className="text-xl line-height-md mb-4">
                {i18n.t('LV.enterId')}
              </span>

              <div className="d-flex flex-column mb-3 md-form">
                <label
                  id="practiceId-value"
                  className="text-soft text-md"
                  htmlFor="practiceId-field"
                >
                  {i18n.t('LV.practiceId')}
                </label>
                <ErrorField
                  id="practiceId-field"
                  type="text"
                  className="form-control"
                  name="practiceId"
                />
              </div>

              <span id="practiceIdDesc-text" className="text-soft mb-4">
                {i18n.t('LV.practiceIdDesc')}
              </span>

              <div className="row">
                <div className="col">
                  <button
                    id="cancelLookup-btn"
                    className="btn btn-lg btn-block btn-secondary"
                    onClick={goToMain}
                    type="button"
                  >
                    {i18n.t('Common.cancel', { context: 'cap' })}
                  </button>
                </div>
                <div className="col">
                  <button
                    id="lookupPractice-btn"
                    className="btn btn-lg btn-block btn-primary"
                    disabled={isSubmitting}
                    aria-disabled={isSubmitting}
                    type="submit"
                  >
                    {i18n.t('Common.next', { context: 'cap' })}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    );
  } else if (atConnectToDetails && practiceDetails) {
    content = (
      <div id={`practiceDetails-${practiceDetails.id}`} className="d-flex flex-column">
        <div className="d-flex flex-column mb-4">
          <span id="confirmHeader-text" className="text-black text-xl line-height-md mb-2">
            {i18n.t('LV.confirmPractice')}
          </span>
          <span id="confirmDesc-text" className="text-soft line-height-xl">
            {i18n.t('LV.confirmPracticeDesc')}
          </span>
        </div>

        <PracticeDetails {...practiceDetails} />
        {error.connectError && (
          <div className="row mt-4">
            <div className="col text-danger">{i18n.t('Errors.maxPracticesJoined')}</div>
          </div>
        )}

        <div className="row mt-4">
          <div className="col">
            <button
              id="cancel-btn"
              className="btn btn-lg btn-block btn-secondary"
              onClick={(e) => connectToPractice(e, true)}
            >
              {i18n.t('Common.cancel', { context: 'cap' })}
            </button>
          </div>
          <div className="col">
            <button
              id="confirm-btn"
              className="btn btn-lg btn-block btn-primary"
              onClick={confirmPracticeConnect}
              disabled={error.submitDisabled}
            >
              {i18n.t('Common.connect', { context: 'cap' })}
            </button>
          </div>
        </div>

        <HtmlTranslation
          id="confirmDisclaimer-text"
          className="text-soft mt-4"
          translationKey="LV.confirmPracticeDisclaimer"
          translationProps={{
            link: `<a id="privacyNotice-link" href="${privacyNoticeUrl}">${i18n.t(
              'Common.privacyNotice'
            )}</a>`,
          }}
        />
      </div>
    );
  } else if (atPending && selectedInvite) {
    content = (
      <div id={`practiceDetails-${currentPracticeId}`} className="d-flex flex-column">
        <div className="d-flex flex-column mb-4">
          <span id="confirmHeader-text" className="text-black text-xl line-height-md mb-2">
            {i18n.t('LV.pendingInvitation')}
          </span>
          <span id="confirmDesc-text" className="text-soft line-height-xl">
            {i18n.t('LV.confirmPracticeDesc')}
          </span>
        </div>

        <PracticeDetails {...selectedInvite} />

        {error.connectError && (
          <div className="row mt-4">
            <div className="col text-danger">{i18n.t('Errors.maxPracticesJoined')}</div>
          </div>
        )}

        <div className="row mt-4">
          <div className="col">
            <button
              id="decline-btn"
              className="btn btn-lg btn-block btn-secondary"
              onClick={(e) => inviteDecline(e, selectedInvite)}
            >
              {i18n.t('Common.decline', { context: 'cap' })}
            </button>
          </div>
          <div className="col">
            <button
              id="accept-btn"
              className="btn btn-lg btn-block btn-primary"
              onClick={(e) => inviteAccept(e, selectedInvite)}
              disabled={error.submitDisabled}
            >
              {i18n.t('Common.accept', { context: 'cap' })}
            </button>
          </div>
        </div>

        <HtmlTranslation
          id="confirmDisclaimer-text"
          className="text-soft mt-4"
          translationKey="LV.confirmPracticeDisclaimer"
          translationProps={{
            link: `<a id="privacyNotice-link" href="${privacyNoticeUrl}">${i18n.t(
              'Common.privacyNotice'
            )}</a>`,
          }}
        />
      </div>
    );
  }

  return (
    <LibreAppWrapper app={libreView} showBack={atMain || !content}>
      {content}
    </LibreAppWrapper>
  );
};

export default connector(LibreView);
